/**
 * Symbolbibliothek für den Platzplan-Editor.
 *
 * Jedes Symbol ist in einer 24×24-Box definiert und wird sowohl im Editor
 * (React `<symbol>` + `<use>`) als auch beim SVG-Export (String) aus denselben
 * Primitiven gerendert. `currentColor` greift die Farbe des `<use>`-Elements ab.
 *
 * Die Typ-Schlüssel müssen zu api/core/site_plan_document.py (SYMBOL_TYPES) passen.
 */

import { createElement, Fragment, type ReactElement } from "react";

export const SYMBOL_BOX = 24;

export type SymbolPrimitive = {
  tag: "path" | "circle" | "rect" | "line" | "ellipse" | "polygon" | "text";
  attrs: Record<string, string | number>;
  text?: string;
};

export type SitePlanSymbolCategory = "nature" | "supply" | "sanitary" | "service" | "site";

export interface SitePlanSymbolDefinition {
  type: string;
  label: string;
  category: SitePlanSymbolCategory;
  /** Standardfarbe (currentColor im Glyph). */
  color: string;
  /** Weißer Kreis hinter dem Glyph – für Piktogramme auf bunten Flächen. */
  badge: boolean;
  primitives: SymbolPrimitive[];
}

export const SYMBOL_CATEGORY_LABELS: Record<SitePlanSymbolCategory, string> = {
  nature: "Natur",
  supply: "Versorgung",
  sanitary: "Sanitär",
  service: "Service",
  site: "Gelände",
};

const STROKE = {
  fill: "none",
  stroke: "currentColor",
  "stroke-width": 1.8,
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
} as const;

function stroke(d: string, extra: Record<string, string | number> = {}): SymbolPrimitive {
  return { tag: "path", attrs: { d, ...STROKE, ...extra } };
}

function filled(d: string, extra: Record<string, string | number> = {}): SymbolPrimitive {
  return { tag: "path", attrs: { d, fill: "currentColor", ...extra } };
}

function glyphText(text: string, size: number, extra: Record<string, string | number> = {}): SymbolPrimitive {
  return {
    tag: "text",
    text,
    attrs: {
      x: 12,
      y: 12,
      "text-anchor": "middle",
      "dominant-baseline": "central",
      "font-family": "system-ui, -apple-system, 'Segoe UI', sans-serif",
      "font-size": size,
      "font-weight": 700,
      fill: "currentColor",
      ...extra,
    },
  };
}

export const SITE_PLAN_SYMBOLS: SitePlanSymbolDefinition[] = [
  {
    type: "tree",
    label: "Baum",
    category: "nature",
    color: "#2e8b3d",
    badge: false,
    primitives: [
      { tag: "rect", attrs: { x: 11, y: 14, width: 2, height: 8, rx: 0.6, fill: "#6b4a2b" } },
      { tag: "circle", attrs: { cx: 12, cy: 9.5, r: 7.5, fill: "currentColor" } },
      { tag: "circle", attrs: { cx: 9.5, cy: 8, r: 2.2, fill: "#ffffff", opacity: 0.25 } },
    ],
  },
  {
    type: "bush",
    label: "Busch / Hecke",
    category: "nature",
    color: "#4caf50",
    badge: false,
    primitives: [
      { tag: "ellipse", attrs: { cx: 12, cy: 15, rx: 9.5, ry: 6, fill: "currentColor" } },
      { tag: "ellipse", attrs: { cx: 8, cy: 11, rx: 5, ry: 4.2, fill: "currentColor" } },
      { tag: "ellipse", attrs: { cx: 15.5, cy: 10, rx: 5, ry: 4.6, fill: "currentColor" } },
    ],
  },
  {
    type: "power",
    label: "Stromkasten",
    category: "supply",
    color: "#d97706",
    badge: true,
    primitives: [
      { tag: "polygon", attrs: { points: "13,3 6,13.5 11,13.5 10,21 18,10.5 13,10.5", fill: "currentColor" } },
    ],
  },
  {
    type: "water",
    label: "Wasserstelle",
    category: "supply",
    color: "#0284c7",
    badge: true,
    primitives: [filled("M12 4c3.2 4.2 6 7.2 6 10.3a6 6 0 0 1-12 0C6 11.2 8.8 8.2 12 4z")],
  },
  {
    type: "dump_station",
    label: "Entsorgung",
    category: "supply",
    color: "#7c3aed",
    badge: true,
    primitives: [filled("M6 6h12l-4.2 6.2V19h-3.6v-6.8z")],
  },
  {
    type: "trash",
    label: "Müll",
    category: "supply",
    color: "#475569",
    badge: true,
    primitives: [
      { tag: "rect", attrs: { x: 8, y: 8.5, width: 8, height: 11, rx: 1, ...STROKE } },
      stroke("M6 8.5h12M10 5.5h4M10.5 12v5M13.5 12v5"),
    ],
  },
  {
    type: "lantern",
    label: "Laterne",
    category: "supply",
    color: "#a16207",
    badge: true,
    primitives: [stroke("M12 3v3M9 6h6l-1 7h-4zM12 13v8M9.5 21h5")],
  },
  {
    type: "wc",
    label: "WC",
    category: "sanitary",
    color: "#334155",
    badge: true,
    primitives: [glyphText("WC", 9.5)],
  },
  {
    type: "shower",
    label: "Dusche",
    category: "sanitary",
    color: "#0ea5e9",
    badge: true,
    primitives: [stroke("M6 11a6 6 0 0 1 12 0M12 5v0M8.5 14.5v2.5M12 14.5v3.5M15.5 14.5v2.5M12 5V3")],
  },
  {
    type: "washing",
    label: "Waschmaschine",
    category: "sanitary",
    color: "#0d9488",
    badge: true,
    primitives: [
      { tag: "rect", attrs: { x: 6, y: 4, width: 12, height: 16, rx: 1.5, ...STROKE } },
      { tag: "circle", attrs: { cx: 12, cy: 13, r: 4, ...STROKE } },
      { tag: "circle", attrs: { cx: 9, cy: 7, r: 0.9, fill: "currentColor" } },
    ],
  },
  {
    type: "reception",
    label: "Rezeption",
    category: "service",
    color: "#b91c1c",
    badge: true,
    primitives: [stroke("M5 12l7-6 7 6v8H5z"), { tag: "rect", attrs: { x: 10, y: 14, width: 4, height: 6, fill: "currentColor" } }],
  },
  {
    type: "info",
    label: "Information",
    category: "service",
    color: "#2563eb",
    badge: true,
    primitives: [glyphText("i", 14, { "font-family": "Georgia, 'Times New Roman', serif", "font-style": "italic" })],
  },
  {
    type: "restaurant",
    label: "Gastronomie / Kiosk",
    category: "service",
    color: "#ea580c",
    badge: true,
    primitives: [stroke("M8 3v8M6 3v4a2 2 0 0 0 4 0V3M8 11v10M16.5 3c-2.5 0-2.5 7-2.5 7v11M14 10h2.5")],
  },
  {
    type: "first_aid",
    label: "Erste Hilfe",
    category: "service",
    color: "#dc2626",
    badge: true,
    primitives: [filled("M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5z")],
  },
  {
    type: "fire_extinguisher",
    label: "Feuerlöscher",
    category: "service",
    color: "#dc2626",
    badge: true,
    primitives: [
      { tag: "rect", attrs: { x: 9, y: 8, width: 6, height: 12.5, rx: 2.2, fill: "currentColor" } },
      stroke("M12 8V5M12 5h4.5M12 5l-4 2"),
    ],
  },
  {
    type: "wifi",
    label: "WLAN",
    category: "service",
    color: "#0891b2",
    badge: true,
    primitives: [
      stroke("M4.5 10.5a11 11 0 0 1 15 0M7.5 13.8a6.5 6.5 0 0 1 9 0M10.3 17a2.8 2.8 0 0 1 3.4 0"),
      { tag: "circle", attrs: { cx: 12, cy: 19.6, r: 1.3, fill: "currentColor" } },
    ],
  },
  {
    type: "parking",
    label: "Parkplatz",
    category: "site",
    color: "#1d4ed8",
    badge: true,
    primitives: [glyphText("P", 13)],
  },
  {
    type: "entrance",
    label: "Ein-/Ausfahrt",
    category: "site",
    color: "#111827",
    badge: true,
    primitives: [stroke("M4.5 12h14M13.5 7l5 5-5 5", { "stroke-width": 2.4 })],
  },
  {
    type: "barrier",
    label: "Schranke",
    category: "site",
    color: "#f59e0b",
    badge: true,
    primitives: [
      { tag: "rect", attrs: { x: 4, y: 6, width: 2.4, height: 12, fill: "#374151" } },
      { tag: "rect", attrs: { x: 4, y: 12.5, width: 16, height: 3, rx: 1, fill: "currentColor" } },
      { tag: "rect", attrs: { x: 9, y: 12.5, width: 3, height: 3, fill: "#ffffff", opacity: 0.8 } },
      { tag: "rect", attrs: { x: 15, y: 12.5, width: 3, height: 3, fill: "#ffffff", opacity: 0.8 } },
    ],
  },
  {
    type: "playground",
    label: "Spielplatz",
    category: "site",
    color: "#db2777",
    badge: true,
    primitives: [stroke("M4.5 5.5h15M7.5 5.5l1 13M16.5 5.5l-1 13M9.5 14h5")],
  },
  {
    type: "bench",
    label: "Sitzbank",
    category: "site",
    color: "#78350f",
    badge: true,
    primitives: [
      stroke("M5 7.5h14"),
      { tag: "rect", attrs: { x: 5, y: 10.5, width: 14, height: 3, rx: 0.8, fill: "currentColor" } },
      stroke("M7.5 13.5v5M16.5 13.5v5"),
    ],
  },
  {
    type: "grill",
    label: "Grillplatz",
    category: "site",
    color: "#374151",
    badge: true,
    primitives: [
      { tag: "ellipse", attrs: { cx: 12, cy: 10.5, rx: 7, ry: 3, fill: "currentColor" } },
      stroke("M7 13l-2.5 7M17 13l2.5 7M12 13.5v6.5"),
    ],
  },
  {
    type: "pool",
    label: "Schwimmbad",
    category: "site",
    color: "#0ea5e9",
    badge: true,
    primitives: [stroke("M4 11.5c2-2 4-2 6 0s4 2 6 0 4-2 4 0M4 16c2-2 4-2 6 0s4 2 6 0 4-2 4 0")],
  },
  {
    type: "bicycle",
    label: "Fahrrad",
    category: "site",
    color: "#16a34a",
    badge: true,
    primitives: [
      { tag: "circle", attrs: { cx: 7, cy: 16, r: 3.5, ...STROKE } },
      { tag: "circle", attrs: { cx: 17, cy: 16, r: 3.5, ...STROKE } },
      stroke("M7 16l4-7h5l1 7M11 9h4M12.5 16l2.5-7"),
    ],
  },
  {
    type: "dog",
    label: "Hundewiese",
    category: "site",
    color: "#92400e",
    badge: true,
    primitives: [
      { tag: "circle", attrs: { cx: 9, cy: 8.5, r: 1.7, fill: "currentColor" } },
      { tag: "circle", attrs: { cx: 15, cy: 8.5, r: 1.7, fill: "currentColor" } },
      { tag: "circle", attrs: { cx: 6, cy: 12.5, r: 1.6, fill: "currentColor" } },
      { tag: "circle", attrs: { cx: 18, cy: 12.5, r: 1.6, fill: "currentColor" } },
      { tag: "ellipse", attrs: { cx: 12, cy: 15.5, rx: 4.2, ry: 3.3, fill: "currentColor" } },
    ],
  },
  {
    type: "marker",
    label: "Markierung",
    category: "site",
    color: "#ef4444",
    badge: false,
    primitives: [
      filled("M12 22.5s7.5-7 7.5-12.5a7.5 7.5 0 1 0-15 0C4.5 15.5 12 22.5 12 22.5z"),
      { tag: "circle", attrs: { cx: 12, cy: 10, r: 2.8, fill: "#ffffff" } },
    ],
  },
];

const BY_TYPE = new Map(SITE_PLAN_SYMBOLS.map((s) => [s.type, s]));

export function getSymbolDefinition(type: string): SitePlanSymbolDefinition {
  return BY_TYPE.get(type) ?? BY_TYPE.get("marker")!;
}

export function symbolDomId(type: string): string {
  return `sp-sym-${getSymbolDefinition(type).type}`;
}

export function symbolsByCategory(): { category: SitePlanSymbolCategory; items: SitePlanSymbolDefinition[] }[] {
  const order: SitePlanSymbolCategory[] = ["nature", "supply", "sanitary", "service", "site"];
  return order.map((category) => ({
    category,
    items: SITE_PLAN_SYMBOLS.filter((s) => s.category === category),
  }));
}

const BADGE_PRIMITIVE: SymbolPrimitive = {
  tag: "circle",
  attrs: { cx: 12, cy: 12, r: 11, fill: "#ffffff", stroke: "currentColor", "stroke-width": 1.4 },
};

export function symbolPrimitives(def: SitePlanSymbolDefinition): SymbolPrimitive[] {
  return def.badge ? [BADGE_PRIMITIVE, ...def.primitives] : def.primitives;
}

// --- React ---------------------------------------------------------------------

const ATTR_TO_PROP: Record<string, string> = {
  "stroke-width": "strokeWidth",
  "stroke-linecap": "strokeLinecap",
  "stroke-linejoin": "strokeLinejoin",
  "text-anchor": "textAnchor",
  "dominant-baseline": "dominantBaseline",
  "font-family": "fontFamily",
  "font-size": "fontSize",
  "font-weight": "fontWeight",
  "font-style": "fontStyle",
};

function toReactProps(attrs: Record<string, string | number>): Record<string, string | number> {
  const props: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(attrs)) {
    props[ATTR_TO_PROP[key] ?? key] = value;
  }
  return props;
}

export function renderSymbolPrimitives(def: SitePlanSymbolDefinition): ReactElement {
  return createElement(
    Fragment,
    null,
    ...symbolPrimitives(def).map((p, index) =>
      createElement(p.tag, { key: index, ...toReactProps(p.attrs) }, p.text),
    ),
  );
}

/** `<defs>` mit allen Symbolen – einmal pro SVG einbinden. */
export function SitePlanSymbolDefs() {
  return (
    <defs>
      {SITE_PLAN_SYMBOLS.map((def) => (
        <symbol key={def.type} id={symbolDomId(def.type)} viewBox={`0 0 ${SYMBOL_BOX} ${SYMBOL_BOX}`}>
          {renderSymbolPrimitives(def)}
        </symbol>
      ))}
    </defs>
  );
}

/** Kleine Vorschau für die Palette. */
export function SitePlanSymbolPreview({ type, size = 28 }: { type: string; size?: number }) {
  const def = getSymbolDefinition(type);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${SYMBOL_BOX} ${SYMBOL_BOX}`}
      style={{ color: def.color }}
      aria-hidden
    >
      {renderSymbolPrimitives(def)}
    </svg>
  );
}

// --- Export (String) -----------------------------------------------------------

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function serializePrimitives(def: SitePlanSymbolDefinition): string {
  return symbolPrimitives(def)
    .map((p) => {
      const attrs = Object.entries(p.attrs)
        .map(([k, v]) => `${k}="${escapeXml(String(v))}"`)
        .join(" ");
      if (p.tag === "text") {
        return `<text ${attrs}>${escapeXml(p.text ?? "")}</text>`;
      }
      return `<${p.tag} ${attrs}/>`;
    })
    .join("");
}

/** `<defs>`-Block nur für die tatsächlich verwendeten Symboltypen. */
export function serializeSymbolDefs(types: Iterable<string>): string {
  const unique = new Set(Array.from(types).map((t) => getSymbolDefinition(t).type));
  if (unique.size === 0) return "";
  const symbols = Array.from(unique)
    .map((type) => {
      const def = getSymbolDefinition(type);
      return `<symbol id="${symbolDomId(type)}" viewBox="0 0 ${SYMBOL_BOX} ${SYMBOL_BOX}">${serializePrimitives(def)}</symbol>`;
    })
    .join("");
  return `<defs>${symbols}</defs>`;
}
